import React, { useState } from "react";
import styles from "../style";
import { tech1, tech2, tech3, tech4, tech5 } from "../assets";

export default function TechnologyListing() {
  const [selectedTech, setSelectedTech] = useState(0);

  const technologies = [
    {
      id: 0,
      title: "AI-Powered Sign Reading",
      subtitle: "Computer Vision Excellence",
      description: "Our advanced computer vision algorithms automatically interpret and extract text from street signs, parking regulations, and road markings with industry-leading accuracy.",
      features: [
        "Multi-language sign detection",
        "Regulation extraction & parsing",
        "Real-time processing capability",
        "Weather-resistant recognition"
      ],
      stats: {
        accuracy: "100%",
        localization_accuracy: "50cm"
      },
      image: tech3,
      gradient: "from-blue-600 to-cyan-500"
    },
    {
      id: 1,
      title: "Precise Asset Localization",
      subtitle: "Centimeter-Level Accuracy", 
      description: "Advanced GPS fusion and visual SLAM technology enables precise mapping of every street asset, creating an accurate digital twin of your infrastructure.",
      features: [
        "Sub-meter location accuracy",
        "Visual SLAM integration",
        "3D spatial mapping",
        "Automatic georeferencing"
      ],
      stats: {
        precision: "<50cm",
        coverage: "360°"
      },
      image: tech5,
      gradient: "from-purple-600 to-pink-500"
    },
    {
      id: 2,
      title: "Vehicle Detection & Analytics",
      subtitle: "Real-time Occupancy Intelligence",
      description: "State-of-the-art deep learning models detect, track, and analyze vehicle presence, providing real-time occupancy data and parking patterns.",
      features: [
        "Multi-vehicle classification",
        "Occupancy duration tracking",
        "Violation detection",
        "Predictive availability"
      ],
      stats: {
        detection: "98%",
        latency: "Real-time",
        vehicles: "20+ types"
      },
      image: tech1,
      gradient: "from-emerald-600 to-teal-500"
    },
    {
      id: 3,
      title: "Intelligent Navigation System",
      subtitle: "Smart Routing Algorithms",
      description: "Proprietary algorithms optimize routes for parking search, maintenance crews, and enforcement, reducing cruising time and operational costs.",
      features: [
        "Dynamic route optimization",
        "Parking probability scoring",
        "Crew dispatch intelligence",
        "Multi-modal integration"
      ],
      stats: {
        efficiency: "60%+",
        coverage: "City-wide",
        updates: "Live"
      },
      image: tech4,
      gradient: "from-orange-600 to-red-500"
    }
  ];

  const techStack = [
    { category: "AI/ML", tools: ["PyTorch", "TensorFlow", "OpenCV", "YOLO"] },
    { category: "Cloud", tools: ["AWS", "Google Cloud", "Docker", "Kubernetes"] },
    { category: "Data", tools: ["PostgreSQL", "Redis", "Apache Kafka", "Elasticsearch"] },
    { category: "APIs", tools: ["REST", "GraphQL", "WebSocket", "gRPC"] }
  ];

  return (
    <section className={`${styles.flexCenter} flex-col ${styles.paddingY} bg-gradient-to-b from-white to-gray-50`}>
      
      {/* Hero Section */}
      <div className="w-full max-w-7xl px-4">
        <div className="relative mb-16 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 text-white shadow-2xl md:p-16">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute h-96 w-96 -left-48 -top-48 rounded-full bg-purple-500 blur-3xl animate-pulse"></div>
            <div className="absolute h-96 w-96 -right-48 -bottom-48 rounded-full bg-blue-500 blur-3xl animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative z-10 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>Cutting-Edge AI Technology Stack</span>
            </div>
            
            <h1 className="mb-6 text-4xl font-bold md:text-5xl lg:text-6xl">
              Technology That
              <span className="block bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                Powers the Future
              </span>
            </h1>
            
            <p className="mx-auto mb-8 max-w-3xl text-lg text-gray-300">
              Transform ordinary dashcam footage into comprehensive street intelligence. 
              Our proprietary AI technology stack combines computer vision, deep learning, 
              and cloud computing to deliver unmatched accuracy and speed.
            </p>
            
            <div className="flex flex-wrap justify-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">100M+</div>
                <div className="text-sm text-gray-400">Frames Processed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">100%</div>
                <div className="text-sm text-gray-400">Accuracy Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400">Online/Offline</div>
                <div className="text-sm text-gray-400">Processing</div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Technologies - All Displayed */}
        <div className="mb-16">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Core Technologies
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Four pillars of innovation that transform street video into actionable intelligence
            </p>
          </div>

          {/* All Technologies Display */}
          <div className="space-y-8">
            {technologies.map((tech, index) => (
              <div key={tech.id} className="overflow-hidden rounded-2xl bg-white shadow-xl hover:shadow-2xl transition-all">
                <div className={`grid ${index % 2 === 0 ? 'md:grid-cols-2' : 'md:grid-cols-2 direction-rtl'}`}>
                  {/* Image - alternates sides */}
                  <div className={`relative bg-gradient-to-br ${tech.gradient} p-1 ${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                    <img 
                      src={tech.image} 
                      alt={tech.title}
                      className="w-full rounded-xl object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
                    {/* Number Badge */}
                    <div className="absolute top-4 left-4 h-12 w-12 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
                      <span className={`text-xl font-bold bg-gradient-to-r ${tech.gradient} bg-clip-text text-transparent`}>
                        {index + 1}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className={`p-8 md:p-10 ${index % 2 !== 0 ? 'md:order-1' : ''}`}>
                    <div className={`mb-4 inline-flex rounded-lg bg-gradient-to-r ${tech.gradient} p-[2px]`}>
                      <span className="rounded-lg bg-white px-4 py-2">
                        <span className={`font-semibold bg-gradient-to-r ${tech.gradient} bg-clip-text text-transparent`}>
                          {tech.subtitle}
                        </span>
                      </span>
                    </div>
                    
                    <h3 className="mb-4 text-2xl font-bold text-gray-900">
                      {tech.title}
                    </h3>
                    
                    <p className="mb-6 text-gray-700 leading-relaxed">
                      {tech.description}
                    </p>
                    
                    {/* Features Grid */}
                    <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {tech.features.map((feature, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Stats */}
                    <div className="flex flex-wrap gap-4 border-t pt-6">
                      {Object.entries(tech.stats).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className={`text-xl font-bold bg-gradient-to-r ${tech.gradient} bg-clip-text text-transparent`}>
                            {value}
                          </span>
                          <span className="text-sm text-gray-600">
                            {key
                              .split('_')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Innovation Metrics */}
        <div className="mb-16 rounded-2xl bg-gradient-to-r from-gray-50 to-white p-8 md:p-12">
          <div className="mb-8 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              Performance That Matters
            </h2>
          </div>
          
          <div className="grid gap-6 md:grid-cols-3">
            <div className="text-center">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold">Lightning Fast</h3>
              <p className="text-gray-600">Process thousands of frames per second with real-time analysis</p>
            </div>
            
            <div className="text-center">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold">Enterprise Secure</h3>
              <p className="text-gray-600">SOC2 compliant with end-to-end encryption</p>
            </div>
            
            <div className="text-center">
              <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold">Infinitely Scalable</h3>
              <p className="text-gray-600">Cloud-native architecture scales with your city</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="rounded-3xl bg-gradient-to-r from-blue-600 to-emerald-600 p-12 text-center text-white shadow-2xl">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl">
            See Our Technology in Action
          </h2>
          <p className="mb-8 text-lg opacity-90">
            Request a technical demo and discover how our AI transforms your street data
          </p>
          <div className="flex flex-wrap justify-center gap-4">
             <a 
                href="#/contact"
                className="inline-block rounded-full bg-white px-8 py-4 font-bold text-gray-900 shadow-lg transition hover:scale-105 hover:shadow-xl"
              >
                Contact us to Schedule Demo
              </a>
          </div>
        </div>
      </div>
    </section>
  );
}